import { Link } from "react-router-dom";
import { BadgeCheck } from "lucide-react";
import peopleBanner from "../assets/people-banner.webp";
import familyOutdoor from "../assets/josue-michel-OMl0o6TSQXU-unsplash.webp";
import familyField from "../assets/nathan-dumlao-4_mJ1TbMK8A-unsplash.webp";
import familyForest from "../assets/ioann-mark-kuznietsov-9QTQFihyles-unsplash.webp";
import familyWalking from "../assets/jessica-rockowitz-5NLCaz2wJXE-unsplash.webp";


export default function Home() {
  return (
    <div className="flex flex-col gap-5 mt-10">
      {/* Hero */}
      <div className="bg-white border border-border rounded-2xl overflow-hidden">
        <div className="px-14 pt-14 pb-5 max-[680px]:px-8 max-[680px]:pt-10">
          <h1 className="font-display text-[42px] font-semibold leading-[1.2] text-primary-dark max-w-[700px]">
            Strengthening the Couple Relationship Through <em className="italic text-primary">Parenting.</em>
          </h1>
        </div>
        <div className="flex items-stretch max-[680px]:flex-col">
          <div className="w-1/3 shrink-0 px-14 py-8 max-[680px]:px-8 max-[680px]:py-6">
            <p className="text-[15px] text-[#555] leading-relaxed">
              The MA &amp; PA Program helps couples — married or unmarried — explore parenting attitudes and skills together, building a shared vision for their family.
            </p>
          </div>
          <div className="my-8 border-l border-border/40 max-[680px]:border-l-0 max-[680px]:border-t max-[680px]:mx-8 max-[680px]:my-0" />
          <div className="w-2/3 px-14 py-8 flex flex-col items-end text-right max-[680px]:px-8 max-[680px]:py-6 max-[680px]:items-start max-[680px]:text-left">
            <p className="font-display text-[26px] font-semibold text-primary-dark leading-snug" style={{ textShadow: '0 2px 12px rgba(91, 75, 138, 0.18)' }}>
              "Strengthening the Couple Relationship Through an Exploration of Parenting Attitudes and Skills."
            </p>
            <span className="text-[13px] text-[#aaa] tracking-[0.04em] block mt-3">MA &amp; PA Program</span>
          </div>
        </div>
        <img src={peopleBanner} alt="Families" className="w-full h-28 object-cover object-top" />
      </div>

      {/* Section label */}
      <div className="text-[10px] font-medium tracking-[0.1em] uppercase text-[#999] px-1 -mb-2">About the Program</div>

      {/* About — two columns: text left, photo right */}
      <div className="grid grid-cols-[1fr_380px] bg-white border border-border rounded-2xl overflow-hidden max-[900px]:grid-cols-1">
        <div className="p-11 px-12">
          <h2 className="font-display text-[24px] font-semibold text-primary-dark mb-4">A curriculum built for couples</h2>
          <p className="text-[15px] text-[#555] leading-relaxed">
            MA &amp; PA focuses on strengthening the couple relationship through a shared exploration of parenting attitudes and skills. This 10-week curriculum draws on the most effective approaches from The Nurturing Father's Program and adds new relationship building activities for spouses and partners.
          </p>
          <p className="text-[15px] text-[#555] leading-relaxed mt-4">
            Through weekly group experiential activities and the MA &amp; PA Journal, each couple develops the attitudes and skills for successful family formation — available in English and Spanish.
          </p>
          <div className="flex flex-wrap gap-2 mt-6">
            <span className="bg-primary-light text-primary text-[12px] font-medium px-3 py-1 rounded-full">Head Start Programs</span>
            <span className="bg-primary-light text-primary text-[12px] font-medium px-3 py-1 rounded-full">Churches</span>
            <span className="bg-primary-light text-primary text-[12px] font-medium px-3 py-1 rounded-full">Schools</span>
            <span className="bg-primary-light text-primary text-[12px] font-medium px-3 py-1 rounded-full">Counseling Centers</span>
            <span className="bg-primary-light text-primary text-[12px] font-medium px-3 py-1 rounded-full">Community Agencies</span>
            <span className="bg-primary-light text-primary text-[12px] font-medium px-3 py-1 rounded-full">State DSS</span>
            <span className="bg-primary-light text-primary text-[12px] font-medium px-3 py-1 rounded-full">Military Families</span>
            <span className="bg-primary-light text-primary text-[12px] font-medium px-3 py-1 rounded-full">Parenting Centers</span>
          </div>
        </div>
        <div className="border-l border-border overflow-hidden max-[900px]:hidden">
          <img src={familyOutdoor} alt="Family" className="w-full h-full object-cover" />
        </div>
      </div>

      {/* What couples gain — own card */}
      <div className="bg-primary-subtle border border-border-accent rounded-2xl p-11 px-12">
        <h3 className="font-display text-[22px] font-semibold text-primary-dark mb-6">What couples gain</h3>
        <div className="grid grid-cols-2 gap-x-12 max-[680px]:grid-cols-1">
          <div className="flex items-center gap-3 py-3 text-[15px] text-[#333] border-b border-border-accent"><BadgeCheck className="text-purple-800" />A shared vision for family life</div>
          <div className="flex items-center gap-3 py-3 text-[15px] text-[#333] border-b border-border-accent"><BadgeCheck className="text-purple-800" />Effective parenting attitudes &amp; skills</div>
          <div className="flex items-center gap-3 py-3 text-[15px] text-[#333] border-b border-border-accent max-[680px]:border-b"><BadgeCheck className="text-purple-800" />Deeper understanding of each other</div>
          <div className="flex items-center gap-3 py-3 text-[15px] text-[#333] border-b border-border-accent"><BadgeCheck className="text-purple-800" />Relationship-building activities</div>
          <div className="flex items-center gap-3 py-3 text-[15px] text-[#333] max-[680px]:border-b max-[680px]:border-border-accent"><BadgeCheck className="text-purple-800" />Conflict resolution as a couple</div>
        </div>
        <Link to="/about" className="inline-block mt-6 text-[13px] font-medium text-primary no-underline hover:underline">
          View full curriculum →
        </Link>
      </div>

      {/* Spanish callout */}
      <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl px-11 py-9 flex justify-between items-center gap-6 max-[680px]:flex-col">
        <div>
          <h3 className="font-display text-[22px] text-white mb-2">Disponible en Español</h3>
          <p className="text-[15px] text-white/75 leading-snug max-w-[560px]">
            The complete MA &amp; PA Program is available in Spanish — Workbook, Facilitator Manual, and all supporting materials for Spanish-speaking couples.
          </p>
        </div>
        <Link to="/contact" className="bg-white text-primary text-[14px] font-medium px-6 py-3 rounded-[20px] transition-opacity hover:opacity-90 no-underline whitespace-nowrap">
          Order Now
        </Link>
      </div>

      {/* Testimonials */}
      <div className="text-[10px] font-medium tracking-[0.1em] uppercase text-[#999] px-1 -mb-2">What Parents Say</div>

      <div className="flex flex-col gap-4">
        <div className="bg-white border border-border rounded-2xl overflow-hidden flex h-72 max-[680px]:flex-col max-[680px]:h-auto">
          <div className="w-80 shrink-0 overflow-hidden max-[680px]:w-full max-[680px]:h-56">
            <img src={familyField} alt="" className="w-full h-full object-cover object-center" />
          </div>
          <div className="p-10 flex flex-col justify-center overflow-hidden">
            <div className="font-display text-[52px] text-border-accent leading-none mb-3">"</div>
            <p className="text-[16px] text-[#444] leading-relaxed italic mb-4">The lessons that I have learned in this class are precious to me and I am blessed just to be here. Lesson(s) of this class are lesson(s) that we would be able to take with us in life and pass it on to our children.</p>
            <div className="text-[13px] text-primary font-medium">Wife and mother, Head Start Program</div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-2xl overflow-hidden flex flex-row-reverse h-72 max-[680px]:flex-col max-[680px]:h-auto">
          <div className="w-80 shrink-0 overflow-hidden max-[680px]:w-full max-[680px]:h-56">
            <img src={familyForest} alt="" className="w-full h-full object-cover object-center" />
          </div>
          <div className="p-10 flex flex-col justify-center overflow-hidden">
            <div className="font-display text-[52px] text-border-accent leading-none mb-3">"</div>
            <p className="text-[16px] text-[#444] leading-relaxed italic mb-4">Should be offered to couples planning to be married. Every married couple should consider it.</p>
            <div className="text-[13px] text-primary font-medium">Wife and mother, Baptist Church</div>
          </div>
        </div>

        <div className="bg-white border border-border rounded-2xl overflow-hidden flex h-72 max-[680px]:flex-col max-[680px]:h-auto">
          <div className="w-80 shrink-0 overflow-hidden max-[680px]:w-full max-[680px]:h-56">
            <img src={familyWalking} alt="" className="w-full h-full object-cover object-top" />
          </div>
          <div className="p-10 flex flex-col justify-center overflow-hidden">
            <div className="font-display text-[52px] text-border-accent leading-none mb-3">"</div>
            <p className="text-[16px] text-[#444] leading-relaxed italic mb-4">This program brought my wife and I closer together. We now have a shared vision for our family and tools to make it real.</p>
            <div className="text-[13px] text-primary font-medium">Father, Community Program</div>
          </div>
        </div>
      </div>
    </div>
  );
}
